/**
 * CollisionDetectionModule
 *
 * Handles planet collision detection and perpendicular position calculation.
 * Extracted from ElementRenderingEngine for single-responsibility.
 */

import { PLANET_DISPLAY_CONSTANTS } from '../../config/PlanetLayerConfig';

// Collision cache entry type
export interface PlanetCacheEntry {
    planetName: string;
    globalDegree: number;
    side: 'inner' | 'outer';
    containerIndex: number;
}

// Position constants shared with main engine
export const POSITION_CONSTANTS = {
    BASE_DISTANCE: 22,
    DEFAULT_INSET_RATIO: 0.5,
    CORNER_HOUSES: [0, 3, 6, 9] as const
};

/**
 * Check if a planet collides with previously positioned planets
 * Uses global degrees instead of local house degrees
 */
export function checkPlanetCollision(
    planetName: string,
    globalDegree: number,
    containerIndex: number,
    globalPlanetCache: PlanetCacheEntry[]
): { side: 'inner' | 'outer', multiplier: number } {
    let sideUsed: 'inner' | 'outer' = 'inner';
    let multiplier = 1; // 1 = inner radius, -1 = outer radius

    if (PLANET_DISPLAY_CONSTANTS.COLLISION_DETECTION_ENABLED) {
        // Check collision with ALL previously positioned planets globally (not just in same house)
        for (const existingPlanet of globalPlanetCache) {
            const degreeDifference = Math.abs(globalDegree - existingPlanet.globalDegree);

            // Handle zodiac wrap-around (e.g., 359 and 1 are actually 2 apart)
            const wrappedDifference = Math.min(degreeDifference, 360 - degreeDifference);

            if (wrappedDifference < PLANET_DISPLAY_CONSTANTS.COLLISION_TOLERANCE_DEGREES && existingPlanet.side === 'inner') {
                multiplier = -1; // Move to outer radius
                sideUsed = 'outer';
                break;
            }
        }

        // Cache this planet's position for future collision checks
        const planetData = { planetName, globalDegree, side: sideUsed, containerIndex };
        globalPlanetCache.push(planetData);
    }

    return { side: sideUsed, multiplier };
}

/**
 * Perpendicular position calculation configuration
 */
export interface PerpendicularPositionConfig {
    containerIndex: number;
    containerBounds: any;
    linePosition: { x: number, y: number };
    yogaDegree: number;
    positionIndex: number;
    planetName?: string;
    useCollisionDetection?: boolean;
    isExternal?: boolean;
    baseDistance?: number;
    insetRatio?: number;
    globalDegree?: number;
}

/**
 * Geometry calculation callbacks that the main engine provides
 */
export interface GeometryCallbacks {
    calculateArcTangent: (containerIndex: number, progress: number, containerBounds: any, insetRatio: number) => { x: number, y: number };
    calculateDegreeLineCoordinatesOld: (containerIndex: number, containerBounds: any, insetRatio: number) => any;
}

function isCornerHouse(containerIndex: number): boolean {
    return POSITION_CONSTANTS.CORNER_HOUSES.includes(containerIndex as 0 | 3 | 6 | 9);
}

function calculateVectorLength(x: number, y: number): number {
    return Math.sqrt(x * x + y * y);
}

function calculateDotProduct(perpDirX: number, perpDirY: number, toCenterX: number, toCenterY: number): number {
    return perpDirX * toCenterX + perpDirY * toCenterY;
}

function normalizeDirection(dotProduct: number, perpDirX: number, perpDirY: number): { x: number, y: number } {
    if (dotProduct < 0) {
        return { x: -perpDirX, y: -perpDirY };
    }
    return { x: perpDirX, y: perpDirY };
}

/**
 * Unified perpendicular position calculation - DRY principle
 * Consolidates all position calculation methods
 */
export function calculatePerpendicularPosition(
    config: PerpendicularPositionConfig,
    globalPlanetCache: PlanetCacheEntry[],
    geometry: GeometryCallbacks
): { x: number, y: number } | null {
    const {
        containerIndex,
        containerBounds,
        linePosition,
        yogaDegree,
        positionIndex,
        planetName = '',
        useCollisionDetection = false,
        isExternal = false,
        baseDistance = POSITION_CONSTANTS.BASE_DISTANCE,
        insetRatio = POSITION_CONSTANTS.DEFAULT_INSET_RATIO,
        globalDegree
    } = config;

    const isCorner = isCornerHouse(containerIndex);

    // Calculate perpendicular direction
    let perpDirX: number, perpDirY: number;

    if (isCorner) {
        const progress = yogaDegree / 30.0;
        const tangentDirection = geometry.calculateArcTangent(
            containerIndex, progress, containerBounds, insetRatio
        );
        perpDirX = -tangentDirection.y;
        perpDirY = tangentDirection.x;
    } else {
        const lineCoords = geometry.calculateDegreeLineCoordinatesOld(containerIndex, containerBounds, insetRatio);
        const lineDirX = lineCoords.endX - lineCoords.startX;
        const lineDirY = lineCoords.endY - lineCoords.startY;
        perpDirX = -lineDirY;
        perpDirY = lineDirX;

        // Ensure direction points toward center for straight houses
        const centerX = containerBounds.width / 2;
        const centerY = containerBounds.height / 2;
        const toCenterX = centerX - linePosition.x;
        const toCenterY = centerY - linePosition.y;

        const dotProduct = calculateDotProduct(perpDirX, perpDirY, toCenterX, toCenterY);
        const normalizedDir = normalizeDirection(dotProduct, perpDirX, perpDirY);
        perpDirX = normalizedDir.x;
        perpDirY = normalizedDir.y;
    }

    // Normalize direction
    const length = calculateVectorLength(perpDirX, perpDirY);
    if (length === 0) return null;

    const normalizedX = perpDirX / length;
    const normalizedY = perpDirY / length;

    // Handle collision detection and positioning
    let directionMultiplier = 1; // 1 = inner radius, -1 = outer radius

    if (useCollisionDetection && planetName) {
        // Use provided global degree or convert local house degree to global zodiac degree
        const actualGlobalDegree = globalDegree ?? (containerIndex * 30 + yogaDegree);
        const collision = checkPlanetCollision(planetName, actualGlobalDegree, containerIndex, globalPlanetCache);
        directionMultiplier = collision.multiplier;
    } else if (!useCollisionDetection) {
        // Fallback to alternating logic
        if (positionIndex % 2 === 1) {
            directionMultiplier = -1;
        }
    }

    // Handle external positioning
    if (isExternal) {
        directionMultiplier = -1; // Always place external elements on outer radius
    }

    // Calculate final position
    const perpendicularX = linePosition.x + normalizedX * baseDistance * directionMultiplier;
    const perpendicularY = linePosition.y + normalizedY * baseDistance * directionMultiplier;

    return { x: perpendicularX, y: perpendicularY };
}
